import { Injectable } from '@angular/core';
import { TimeSheetEntity } from '../models/entities/timesheet.entity';

@Injectable({
  providedIn: 'root'
})
export class DateHelperService {


  private daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  public daysOfHours = [0, 0, 0, 0, 0, 0, 0];
  constructor() { }
  public formatMinutes(minutesTotal: number): string {
    const diffInMilliseconds = minutesTotal * 60 * 1000;
    const hours = Math.floor(diffInMilliseconds / 1000 / 60 / 60);
    const minutes = Math.floor(diffInMilliseconds / 1000 / 60) % 60;

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }


  public calculateTimeDifferenceMinutes(from: string, to: string): number {
    // Input validation
    if (!from || !to) {
      console.error('Invalid time inputs:', { from, to });
      return 0;
    }
    
    
    
    try {
      // Ensure the time strings are in the format HH:MM
      const fromParts = from.split(':');
      const toParts = to.split(':');
      
      if (fromParts.length < 2 || toParts.length < 2) {
        console.error('Invalid time format:', { from, to });
        return 0;
      }
      
      // Create date objects with the time parts
      const fromHours = parseInt(fromParts[0], 10);
      const fromMinutes = parseInt(fromParts[1], 10);
      const toHours = parseInt(toParts[0], 10);
      const toMinutes = parseInt(toParts[1], 10);
      
      // Validate parsed values
      if (isNaN(fromHours) || isNaN(fromMinutes) || isNaN(toHours) || isNaN(toMinutes)) {
        console.error('Failed to parse time components:', { fromHours, fromMinutes, toHours, toMinutes });
        return 0;
      }
      
      // Create date objects for comparison
      let fromTime = new Date(2000, 0, 1, fromHours, fromMinutes, 0);
      let toTime = new Date(2000, 0, 1, toHours, toMinutes, 0);
      
      console.log('Parsed times:', { 
        fromTime: fromTime.toISOString(), 
        toTime: toTime.toISOString() 
      });
      
      // Check if the 'to' time is exactly the same as 'from' time
      if (toTime.getTime() === fromTime.getTime()) {
        return 0; // No difference if times are identical
      }
      
      // Check if 'to' time is earlier than 'from' time, implying 'to' is on the next day
      if (toTime < fromTime) {
        // Set 'to' time to the next day
        toTime.setDate(toTime.getDate() + 1);
      }
      
      // Calculate the difference in milliseconds
      const diffInMilliseconds = toTime.getTime() - fromTime.getTime();
      // Convert milliseconds to minutes
      const minutes = Math.floor(diffInMilliseconds / 1000 / 60);
      
      
      return minutes;
    } catch (e) {
      console.error('Error in calculateTimeDifferenceMinutes:', e);
      return 0;
    }
  }

  public formatMinutesWithDays(minutesTotal: number): string {
    const diffInMilliseconds = minutesTotal * 60 * 1000;
    const days = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffInMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffInMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
  
    let result = '';
    if (days > 0) {
      result += `${days}d `;
    }
    if (hours > 0) {
      result += `${hours}h `;
    }
    result += `${minutes}m`;
  
    return result.trim();
  }
  
  public groupDataByDayOfWeek(data: TimeSheetEntity[]): {
    [key: string]: TimeSheetEntity[];
  } {
    const groupedData: { [key: string]: TimeSheetEntity[] } = {};

    this.daysOfWeek.forEach((day) => {
      groupedData[day] = [];
    });

    data.forEach((item) => {
      const date = new Date(item.date);

      // Check if the item falls within the current week

      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
      groupedData[dayOfWeek].push(item);
      groupedData[dayOfWeek] = this.sortEntries(groupedData[dayOfWeek]);
      let indexWeekDay = this.daysOfWeek.indexOf(dayOfWeek);
      this.daysOfHours[indexWeekDay] += item.total;
    });

    return groupedData;
  }

  public addMonths(startDate: Date, amount: number) {
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + amount);
    return date;    
    
  }
  private sortEntries(entries: any[]): any[] {
    return entries.sort((a, b) => {
      const timeA = new Date(`2000-01-01 ${a.from}`);
      const timeB = new Date(`2000-01-01 ${b.from}`);
      if (timeA < timeB) {
        return -1;
      } else if (timeA > timeB) {
        return 1;
      }

      const endTimeA = new Date(`2000-01-01 ${a.to}`);
      const endTimeB = new Date(`2000-01-01 ${b.to}`);
      if (endTimeA < endTimeB) {
        return -1;
      } else if (endTimeA > endTimeB) {
        return 1;
      }

      return 0;
    });
  }
  public getDateByWeekDay(weekday: string, startDate: Date): Date {
    const startDayIndex = this.daysOfWeek.indexOf('Sun'); // Index of the start day (Sunday)
    const weekdayIndex = this.daysOfWeek.indexOf(weekday); // Index of the requested weekday
    const dayDiff = weekdayIndex - startDayIndex; // Difference in days

    // Clone the start date to avoid modifying the original date
    const currentDate = new Date(startDate);

    // Calculate the target date by adding the day difference to the start date
    currentDate.setDate(currentDate.getDate() + dayDiff);

    return currentDate;
  }
  public isDateInRange(startDate: Date, endDate: Date) {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Set the time to 00:00:00 for accurate comparison
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return today >= start && today <= end;
  }
  public isToday(selectedDate: Date) {
    
    const start = new Date(selectedDate);
    const end = new Date();

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return start.getTime() === end.getTime();
  }
  public weekStartDate(startDate: Date): Date{
    const previousWeekStartDate = new Date(startDate);
    previousWeekStartDate.setDate(previousWeekStartDate.getDate() - 7);
    return previousWeekStartDate;
  }

  public weekEndDate(endDate: Date): Date{
     const previousWeekEndDate = new Date(endDate);
    previousWeekEndDate.setDate(previousWeekEndDate.getDate() + 7);
    return previousWeekEndDate;
  }

  public getTodaysWeekDay(): string {
    const date = new Date();
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
    return dayOfWeek;
  }

  public getWeekStartDate(date: Date): Date {
    const dayOfWeek = date.getDay();
    const diff = date.getDate() - dayOfWeek; // Adjust for Sunday start of the week
    var weekStartDate = new Date(date.getFullYear(), date.getMonth(), diff);
    weekStartDate.setHours(0, 0, 0, 0);
    return weekStartDate;
  }

  public getWeekEndDate(date: Date): Date {
    const weekStartDate = this.getWeekStartDate(date);
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekEndDate.getDate() + 6); // Add 6 days to get the end of the week
    weekEndDate.setHours(23, 59, 59, 999);
    return weekEndDate;
  }

  public getWeekdayDate(startDate: Date, endDate: Date, daySelected: string): Date {
    let selectedWeekday: number = this.daysOfWeek.indexOf(daySelected);

    if (startDate > endDate) {
      throw new Error('startDate must be less than or equal to endDate.');
    }

    const daysDiff = (selectedWeekday - startDate.getDay() + 7) % 7;
    const selectedDate = new Date(
      startDate.getTime() + daysDiff * 24 * 60 * 60 * 1000
    );

    if (selectedDate < startDate || selectedDate > endDate) {
      throw new Error(
        'No occurrence of the selected weekday found within the range.'
      );
    }

    return selectedDate;
  }

  public getMonthDates(date: Date): { firstDate: Date, lastDate: Date } {
    const year: number = date.getFullYear();
    const month: number = date.getMonth();
  
    // Get the first date of the month
    const firstDate: Date = new Date(year, month, 1);
  
    // Get the last date of the month
    const lastDate: Date = new Date(year, month + 1, 0);
  
    return { firstDate, lastDate };
  }

  public getFirstDateOfPreviousMonth(currentDate: Date): Date {

    const previousMonth: number = currentDate.getMonth() - 1;
    const year: number = currentDate.getFullYear();
  
    // Handle the case of January
    if (previousMonth === -1) {
      return new Date(year - 1, 11, 1); // December of the previous year
    }
  
    return new Date(year, previousMonth, 1);
  }

  public getFirstDateOfNextMonth(): Date {
    const currentDate: Date = new Date();
    const nextMonth: number = currentDate.getMonth() + 1;
    const year: number = currentDate.getFullYear();
  
    // Handle the case of December
    if (nextMonth === 12) {
      return new Date(year + 1, 0, 1); // January of the next year
    }
  
    return new Date(year, nextMonth, 1);
  }

  /**
   * Calculates and formats the time remaining until a due date
   * @param dueDate The due date to calculate time remaining for
   * @returns An object with the formatted string and status (positive, negative, or neutral)
   */
  public formatDueDate(dueDate: Date | null): { text: string, status: 'positive' | 'negative' | 'neutral' } {
    if (!dueDate) {
      return { text: '', status: 'neutral' };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueDateObj = new Date(dueDate);
    dueDateObj.setHours(0, 0, 0, 0);
    
    // Calculate difference in milliseconds
    const diffMs = dueDateObj.getTime() - today.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // If due date is today
    if (diffDays === 0) {
      return { text: 'Due today', status: 'neutral' };
    }
    
    // If due date is in the past
    if (diffDays < 0) {
      const absDiffDays = Math.abs(diffDays);
      
      if (absDiffDays === 1) {
        return { text: 'Overdue by 1 day', status: 'negative' };
      } else if (absDiffDays < 7) {
        return { text: `Overdue by ${absDiffDays} days`, status: 'negative' };
      } else if (absDiffDays < 30) {
        const weeks = Math.floor(absDiffDays / 7);
        return { text: `Overdue by ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`, status: 'negative' };
      } else if (absDiffDays < 365) {
        const months = Math.floor(absDiffDays / 30);
        return { text: `Overdue by ${months} ${months === 1 ? 'month' : 'months'}`, status: 'negative' };
      } else {
        const years = Math.floor(absDiffDays / 365);
        return { text: `Overdue by ${years} ${years === 1 ? 'year' : 'years'}`, status: 'negative' };
      }
    }
    
    // If due date is in the future
    if (diffDays === 1) {
      return { text: 'Due tomorrow', status: 'positive' };
    } else if (diffDays < 7) {
      return { text: `Due in ${diffDays} days`, status: 'positive' };
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return { text: `Due in ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`, status: 'positive' };
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return { text: `Due in ${months} ${months === 1 ? 'month' : 'months'}`, status: 'positive' };
    } else {
      const years = Math.floor(diffDays / 365);
      return { text: `Due in ${years} ${years === 1 ? 'year' : 'years'}`, status: 'positive' };
    }
  }
}